import { extractAccessTokenFromLoginJson } from '../api/authLoginResponse';
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import { extractRefreshTokenFromUnknown } from '../api/authRefresh';
import {
  formatHttpErrorDetails,
  formatThrownErrorDetails,
  friendlyHttpFailureSummary,
  friendlyNetworkErrorMessage,
} from '../api/httpErrorFormat';
import { fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { supportsOtpLiteRegister } from './authEntryPolicy';
import type { LoginOption } from './loginConstants';
import {
  fetchRouteForPath,
  friendlyOtpSendFailureSummary,
  isDedicatedOtpApiPath,
  isOtpRouteMissingOnServer,
  isOtpUserMissingError,
  normalizeOtpCode,
} from './emailOtpAuth';
import { isValidE164Phone, normalizePhoneForWire, normalizePhoneToE164 } from '../utils/phoneE164';

export { normalizeOtpCode, friendlyOtpSendFailureSummary };

export type PhoneOtpApiResult = {
  ok: boolean;
  status: number;
  parsed: unknown;
  responseText: string;
  urlUsed: string;
  response: Response;
  accessToken: string | null;
  refreshToken: string | null;
  pathUsed: string;
};

function pathCandidates(configured: string | undefined, defaults: readonly string[]): string[] {
  const paths = [configured?.trim(), ...defaults].filter((value): value is string => Boolean(value?.trim()));
  return Array.from(new Set(paths));
}

function phoneLoginBasePath(loginType: LoginOption): string {
  const configured = process.env.REACT_APP_PHONE_LOGIN_PATH?.trim();
  if (configured) {
    return configured.replace(/\/+$/, '');
  }
  if (loginType === 'private client' || loginType === 'business client' || loginType === 'business worker') {
    return 'login/clients/phone';
  }
  return 'login/assetsowners/phone';
}

function phoneRegisterBasePath(loginType: LoginOption): string | null {
  const configured = process.env.REACT_APP_PHONE_REGISTER_PATH?.trim();
  if (configured) {
    return configured.replace(/\/+$/, '');
  }
  if (loginType === 'private client') {
    return 'register/PrivateClient/phone';
  }
  if (loginType === 'business client') {
    return 'register/BusinessClient/phone';
  }
  if (loginType === 'business worker') {
    return 'register/BusinessWorker/phone';
  }
  if (loginType === 'asset owner' || loginType === 'business asset owner' || loginType === 'private asset owner') {
    return 'register/AssetOwner/phone';
  }
  return null;
}

function phoneOtpLoginSendPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_PHONE_OTP_SEND_PATH?.trim();
  const base = phoneLoginBasePath(loginType);
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': [`${base}/otp/send`, 'login/clients/phone/otp/send', 'login/PrivateClient/phone/otp/send'],
    'business client': [`${base}/otp/send`, 'login/BusinessClient/phone/otp/send', 'login/clients/phone/otp/send'],
    'business worker': [`${base}/otp/send`, 'login/BusinessWorker/phone/otp/send', 'login/clients/phone/otp/send'],
    'asset owner': [`${base}/otp/send`, 'login/AssetOwner/phone/otp/send', 'login/assetsowners/phone/otp/send'],
    'business asset owner': [
      `${base}/otp/send`,
      'login/AssetOwner/phone/otp/send',
      'login/BusinessAssetOwner/phone/otp/send',
      'login/assetsowners/phone/otp/send',
    ],
    'private asset owner': [
      `${base}/otp/send`,
      'login/AssetOwner/phone/otp/send',
      'login/PrivateAssetOwner/phone/otp/send',
      'login/assetsowners/phone/otp/send',
    ],
  };
  return pathCandidates(configured, byRole[loginType] ?? [`${base}/otp/send`]);
}

function phoneOtpRegisterSendPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_PHONE_OTP_REGISTER_SEND_PATH?.trim();
  const base = phoneRegisterBasePath(loginType);
  if (!base) {
    return pathCandidates(configured, []);
  }
  return pathCandidates(configured, [`${base}/otp/send`]);
}

function phoneOtpLoginVerifyPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_PHONE_OTP_VERIFY_PATH?.trim();
  const base = phoneLoginBasePath(loginType);
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': [`${base}/otp/verify`, 'login/clients/phone/otp/verify', 'login/PrivateClient/phone/otp/verify'],
    'business client': [
      `${base}/otp/verify`,
      'login/BusinessClient/phone/otp/verify',
      'login/clients/phone/otp/verify',
    ],
    'business worker': [
      `${base}/otp/verify`,
      'login/BusinessWorker/phone/otp/verify',
      'login/clients/phone/otp/verify',
    ],
    'asset owner': [`${base}/otp/verify`, 'login/AssetOwner/phone/otp/verify', 'login/assetsowners/phone/otp/verify'],
    'business asset owner': [
      `${base}/otp/verify`,
      'login/AssetOwner/phone/otp/verify',
      'login/BusinessAssetOwner/phone/otp/verify',
      'login/assetsowners/phone/otp/verify',
    ],
    'private asset owner': [
      `${base}/otp/verify`,
      'login/AssetOwner/phone/otp/verify',
      'login/PrivateAssetOwner/phone/otp/verify',
      'login/assetsowners/phone/otp/verify',
    ],
  };
  return pathCandidates(configured, byRole[loginType] ?? [`${base}/otp/verify`]);
}

function phoneOtpRegisterVerifyPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_PHONE_OTP_REGISTER_VERIFY_PATH?.trim();
  const base = phoneRegisterBasePath(loginType);
  if (!base) {
    return pathCandidates(configured, []);
  }
  return pathCandidates(configured, [`${base}/otp/verify`]);
}

/** @internal tests */
export function phoneOtpLoginSendPathCandidatesForTests(loginType: LoginOption): string[] {
  return phoneOtpLoginSendPathCandidates(loginType);
}

/** @internal tests */
export function phoneOtpLoginVerifyPathCandidatesForTests(loginType: LoginOption): string[] {
  return phoneOtpLoginVerifyPathCandidates(loginType);
}

export function isPhoneOtpEnabled(): boolean {
  const flag = process.env.REACT_APP_PHONE_OTP_ENABLED?.trim().toLowerCase();
  return flag !== 'false' && flag !== '0';
}

/** Israeli mobile in local form (05XXXXXXXX) when possible; otherwise E.164 wire format. */
export function normalizePhoneForOtpWire(value: string): string {
  const e164 = normalizePhoneToE164(value);
  if (e164?.startsWith('+972')) {
    const local = `0${e164.slice(4)}`;
    if (/^05\d{8}$/.test(local)) {
      return local;
    }
  }
  const wired = normalizePhoneForWire(value);
  if (wired) {
    return wired;
  }
  return value.trim();
}

export function isValidOtpPhone(value: string): boolean {
  const e164 = normalizePhoneToE164(value);
  if (!e164) {
    return false;
  }
  if (e164.startsWith('+972')) {
    return /^\+9725\d{8}$/.test(e164);
  }
  return isValidE164Phone(e164);
}

function phoneSendBodies(phoneNumber: string): Record<string, unknown>[] {
  return [
    { phone_number: phoneNumber, purpose: 'login' },
    { phone_number: phoneNumber },
    { phone: phoneNumber, purpose: 'login' },
    { phone: phoneNumber },
  ];
}

function phoneVerifyBodies(phoneNumber: string, code: string): Record<string, unknown>[] {
  return [
    { phone_number: phoneNumber, code, purpose: 'login' },
    { phone_number: phoneNumber, code },
    { phone: phoneNumber, code, purpose: 'login' },
    { phone: phoneNumber, code },
  ];
}

function shouldTryNextOtpLoginSendCandidate(
  path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return true;
  }
  if (path.startsWith('login/') && isOtpUserMissingError(status, responseText)) {
    return true;
  }
  if (isDedicatedOtpApiPath(path)) {
    return false;
  }
  return status === 404 || status === 401;
}

function shouldTryNextOtpRegisterSendCandidate(
  _path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  return isOtpRouteMissingOnServer(status, responseText);
}

function shouldTryNextOtpLoginVerifyCandidate(
  path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return true;
  }
  if (path.startsWith('login/') && isOtpUserMissingError(status, responseText)) {
    return true;
  }
  if (isDedicatedOtpApiPath(path)) {
    return false;
  }
  return status === 404 || status === 401;
}

function shouldTryNextOtpRegisterVerifyCandidate(
  _path: string,
  status: number,
  responseText: string,
  hasMorePaths: boolean,
): boolean {
  if (!hasMorePaths) {
    return false;
  }
  return isOtpRouteMissingOnServer(status, responseText);
}

function recordOtpFailure(
  result: PhoneOtpApiResult,
  body: Record<string, unknown>,
): { friendly: string; details: string | null } {
  return {
    friendly: friendlyHttpFailureSummary(result.status, '', result.responseText),
    details: formatHttpErrorDetails({
      requestUrl: result.urlUsed,
      requestPayload: body,
      response: result.response,
      responseText: result.responseText,
    }),
  };
}

async function postJson(
  apiPathTail: string,
  body: Record<string, unknown>,
  route: 'api' | 'login',
): Promise<PhoneOtpApiResult> {
  const { response, urlUsed } = await fetchWithDevProxyFallback(
    apiPathTail,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    route,
  );
  const responseText = await response.text();
  let parsed: unknown = null;
  if (responseText.trim()) {
    try {
      parsed = JSON.parse(responseText) as unknown;
    } catch {
      parsed = null;
    }
  }
  const accessToken = parsed ? extractAccessTokenFromLoginJson(parsed) : null;
  const refreshToken = parsed ? extractRefreshTokenFromUnknown(parsed) : null;
  return {
    ok: isSuccessfulHttpStatus(response.status),
    status: response.status,
    parsed,
    responseText,
    urlUsed,
    response,
    accessToken,
    refreshToken,
    pathUsed: apiPathTail,
  };
}

function phoneOtpSendPathsInOrder(loginType: LoginOption): string[] {
  return [
    ...phoneOtpLoginSendPathCandidates(loginType),
    ...(supportsOtpLiteRegister(loginType) ? phoneOtpRegisterSendPathCandidates(loginType) : []),
  ];
}

function phoneOtpVerifyPathsInOrder(loginType: LoginOption): string[] {
  return [
    ...phoneOtpLoginVerifyPathCandidates(loginType),
    ...(supportsOtpLiteRegister(loginType) ? phoneOtpRegisterVerifyPathCandidates(loginType) : []),
  ];
}

type PhoneOtpSendAttempt = PhoneOtpSendOutcome & { sawUserMissing?: boolean };

async function tryPhoneOtpSend(phoneNumber: string, loginType: LoginOption): Promise<PhoneOtpSendAttempt> {
  const paths = phoneOtpSendPathsInOrder(loginType);
  let lastFriendly = 'Could not send a sign-in code.';
  let lastDetails: string | null = null;
  let sawUserMissing = false;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const hasMorePaths = index < paths.length - 1;
    for (const body of phoneSendBodies(phoneNumber)) {
      const result = await postJson(path, body, fetchRouteForPath(path));
      if (result.ok) {
        return { ok: true };
      }

      const failure = recordOtpFailure(result, body);
      lastFriendly = failure.friendly;
      lastDetails = failure.details;

      if (isOtpUserMissingError(result.status, result.responseText)) {
        sawUserMissing = true;
      }

      const tryNext = path.startsWith('register/')
        ? shouldTryNextOtpRegisterSendCandidate(path, result.status, result.responseText, hasMorePaths)
        : shouldTryNextOtpLoginSendCandidate(path, result.status, result.responseText, hasMorePaths);

      if (!tryNext) {
        return {
          ok: false,
          friendly: lastFriendly,
          details: lastDetails,
          apiNotDeployed: isOtpRouteMissingOnServer(result.status, result.responseText),
          sawUserMissing,
        };
      }
    }
  }

  return {
    ok: false,
    friendly: lastFriendly,
    details: lastDetails,
    apiNotDeployed: false,
    sawUserMissing,
  };
}

export type PhoneOtpSendOutcome =
  | { ok: true }
  | { ok: false; friendly: string; details: string | null; apiNotDeployed: boolean };

export async function sendPhoneOtpCode(
  phone: string,
  loginType: LoginOption = 'private client',
): Promise<PhoneOtpSendOutcome> {
  const normalizedPhone = normalizePhoneForOtpWire(phone);

  try {
    const result = await tryPhoneOtpSend(normalizedPhone, loginType);
    if (result.ok) {
      return { ok: true };
    }
    return {
      ok: false,
      friendly: result.friendly,
      details: result.details,
      apiNotDeployed: result.apiNotDeployed,
    };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
      apiNotDeployed: false,
    };
  }
}

export type PhoneOtpVerifyOutcome =
  | { ok: true; accessToken: string; refreshToken: string | null; parsed: unknown }
  | { ok: false; friendly: string; details: string | null };

async function tryPhoneOtpVerify(
  phoneNumber: string,
  code: string,
  loginType: LoginOption,
): Promise<PhoneOtpVerifyOutcome & { sawUserMissing?: boolean }> {
  const paths = phoneOtpVerifyPathsInOrder(loginType);
  let lastFriendly = 'Could not verify the sign-in code.';
  let lastDetails: string | null = null;
  let sawUserMissing = false;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const hasMorePaths = index < paths.length - 1;
    for (const body of phoneVerifyBodies(phoneNumber, code)) {
      const result = await postJson(path, body, fetchRouteForPath(path));
      if (result.ok && result.accessToken) {
        return {
          ok: true,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          parsed: result.parsed,
        };
      }
      if (result.ok) {
        continue;
      }
      const failure = recordOtpFailure(result, body);
      lastFriendly = failure.friendly;
      lastDetails = failure.details;
      if (isOtpUserMissingError(result.status, result.responseText)) {
        sawUserMissing = true;
      }

      const tryNext = path.startsWith('register/')
        ? shouldTryNextOtpRegisterVerifyCandidate(path, result.status, result.responseText, hasMorePaths)
        : shouldTryNextOtpLoginVerifyCandidate(path, result.status, result.responseText, hasMorePaths);

      if (!tryNext) {
        return { ok: false, friendly: lastFriendly, details: lastDetails, sawUserMissing };
      }
    }
  }

  return { ok: false, friendly: lastFriendly, details: lastDetails, sawUserMissing };
}

export async function verifyPhoneOtpCode(
  phone: string,
  code: string,
  loginType: LoginOption,
): Promise<PhoneOtpVerifyOutcome> {
  const normalizedPhone = normalizePhoneForOtpWire(phone);
  const normalizedCode = normalizeOtpCode(code);

  try {
    const result = await tryPhoneOtpVerify(normalizedPhone, normalizedCode, loginType);
    if (result.ok) {
      return result;
    }
    return { ok: false, friendly: result.friendly, details: result.details };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
    };
  }
}

export function friendlyPhoneOtpSendFailureSummary(
  friendly: string,
  options?: { userNotRegistered?: string; apiNotDeployed?: string; apiNotDeployedFlag?: boolean },
): string {
  if (options?.apiNotDeployedFlag && options.apiNotDeployed) {
    return options.apiNotDeployed;
  }
  if (/user does not exist|user not found|account not found|not registered/i.test(friendly)) {
    return options?.userNotRegistered ?? friendly;
  }
  return friendly;
}
